/**
 * Shared definitions of the exception-noise classes both error-tracking paths
 * (Sentry and posthog-js) agree to drop, kept as pure functions so they can be
 * unit-tested without a browser.
 *
 * 1. Synthetic `CustomEvent` rejections — when a Promise is rejected with a DOM
 *    event object (e.g. a `CustomEvent`) instead of an `Error`, the browser's
 *    global `unhandledrejection` handler cannot extract a message or a stack
 *    trace, so it synthesizes a placeholder exception with
 *    `mechanism.synthetic === true` and a value like
 *    `CustomEvent captured as exception with keys: isTrusted`.
 *
 * 2. Firefox-for-iOS injected userscripts — Firefox iOS runs its own content
 *    scripts inside a WKWebView under a `window.__firefox__` namespace. Before
 *    that namespace exists they throw genuine `ReferenceError`/`TypeError`s
 *    (`Can't find variable: __firefox__`, `window.__firefox__.reader`,
 *    `window.__firefox__.refresh_youtube_quality_<UUID>`). None of this is our
 *    code, but WebKit reports the frame as `global code` at line 1 of our own
 *    document, so URL- and mechanism-based guards miss it — the message is the
 *    only usable discriminator.
 *
 * 3. ResizeObserver loop notifications — Chromium fires `ResizeObserver loop
 *    completed with undelivered notifications` as a `window.onerror` message
 *    when a resize callback mutates layout and the observer runs out of delivery
 *    budget for the frame. It is emitted by the browser, not thrown by our code,
 *    so it arrives with no stack trace and no attributable source frame; nothing
 *    in this repo constructs a `ResizeObserver`, so the trigger is a third-party
 *    UI dependency. URL-based `denyUrls` misses it (no frame URL) — the message
 *    is the only usable discriminator.
 *
 * Every occurrence of any class carries no traceable fault; each opens a fresh
 * error-tracking issue for nothing. Sentry and PostHog share these predicates
 * so the two paths never drift on what counts as noise.
 */

/**
 * Minimal structural shape common to a Sentry `exception.values[]` entry and a
 * PostHog `$exception_list[]` entry — both expose the exception `type` (the DOM
 * constructor name for a coerced event, e.g. `'CustomEvent'`), the `value`
 * (the exception message) and a `mechanism.synthetic` flag. Covers both
 * genuinely synthetic entries (CustomEvent rejections) and real
 * `ReferenceError`/`TypeError`s (Firefox-injected userscripts) that these
 * predicates classify as untraceable noise.
 */
export type ErrorTrackingExceptionEntry = {
  type?: string;
  value?: string;
  mechanism?: { synthetic?: boolean };
};

/**
 * True when a single exception entry is a synthetic, non-`Error`
 * `CustomEvent` — the stackless placeholder both SDKs emit for a Promise
 * rejected with a DOM event object.
 */
export function isSyntheticCustomEventException(
  entry: ErrorTrackingExceptionEntry,
): boolean {
  return entry.type === 'CustomEvent' && entry.mechanism?.synthetic === true;
}

/**
 * True when `entries` is non-empty and *every* entry is a synthetic
 * `CustomEvent` rejection — i.e. the whole event is this noise class and
 * nothing traceable is lost by dropping it. An empty or missing list returns
 * false, so events with no exception values are never dropped by this guard.
 */
export function isAllSyntheticCustomEventExceptions(
  entries: readonly ErrorTrackingExceptionEntry[] | undefined | null,
): boolean {
  return (
    !!entries &&
    entries.length > 0 &&
    entries.every(isSyntheticCustomEventException)
  );
}

/**
 * Matches the message of any exception thrown by Firefox for iOS's own injected
 * `window.__firefox__` userscripts. The namespace token is stable across every
 * handler (`__firefox__`, `__firefox__.reader`,
 * `__firefox__.refresh_youtube_quality_<UUID>`, …) even though the per-install
 * UUID suffixes are not, so the token is the reliable discriminator.
 */
const FIREFOX_INJECTED_MESSAGE_RE = /__firefox__/;

/**
 * True when a single exception entry's message names the `window.__firefox__`
 * namespace — i.e. it came from a Firefox-for-iOS injected userscript, not our
 * code. The exception `type` is a genuine `ReferenceError`/`TypeError`, so the
 * message is the only usable discriminator.
 */
export function isFirefoxInjectedException(
  entry: ErrorTrackingExceptionEntry,
): boolean {
  return !!entry.value && FIREFOX_INJECTED_MESSAGE_RE.test(entry.value);
}

/**
 * True when `entries` is non-empty and *every* entry is a Firefox-injected
 * userscript exception — i.e. the whole event is this noise class and nothing
 * traceable is lost by dropping it. An empty or missing list returns false, so
 * events with no exception values are never dropped by this guard.
 */
export function isAllFirefoxInjectedExceptions(
  entries: readonly ErrorTrackingExceptionEntry[] | undefined | null,
): boolean {
  return (
    !!entries && entries.length > 0 && entries.every(isFirefoxInjectedException)
  );
}

/**
 * Matches the message Chromium emits for a ResizeObserver that ran out of
 * delivery budget for the frame. Both the standard
 * `ResizeObserver loop completed with undelivered notifications` and the older
 * `ResizeObserver loop limit exceeded` wording are covered.
 */
const RESIZE_OBSERVER_LOOP_MESSAGE_RE = /ResizeObserver loop/;

/**
 * True when a single exception entry's message is a ResizeObserver loop
 * notification — a browser-emitted `window.onerror` artifact with no stack
 * trace, not an application fault. The message is the only usable discriminator.
 */
export function isResizeObserverLoopException(
  entry: ErrorTrackingExceptionEntry,
): boolean {
  return !!entry.value && RESIZE_OBSERVER_LOOP_MESSAGE_RE.test(entry.value);
}

/**
 * True when `entries` is non-empty and *every* entry is a ResizeObserver loop
 * notification — i.e. the whole event is this noise class and nothing traceable
 * is lost by dropping it. An empty or missing list returns false, so events
 * with no exception values are never dropped by this guard.
 */
export function isAllResizeObserverLoopExceptions(
  entries: readonly ErrorTrackingExceptionEntry[] | undefined | null,
): boolean {
  return (
    !!entries &&
    entries.length > 0 &&
    entries.every(isResizeObserverLoopException)
  );
}
